/* Small timing primitives behind the configurator's motion/feedback hooks. Pure (timers only), so
   they are unit-tested without a DOM; the React hooks in hooks.ts wrap them. */

export interface DelayedFlagOptions {
  /** The raw flag must stay on this long before the delayed flag turns on. */
  delayMs?: number;
  /** Once on, the delayed flag stays on at least this long (no flash). */
  minVisibleMs?: number;
  now?: () => number;
}

/**
 * Debounces a busy flag for display: `set(true)` shows only after `delayMs` of continuous
 * busy, and once shown it stays at least `minVisibleMs`. Short blips never show at all.
 */
export function createDelayedFlag(onChange: (on: boolean) => void, opts: DelayedFlagOptions = {}) {
  const { delayMs = 250, minVisibleMs = 400, now = () => Date.now() } = opts;
  let shown = false;
  let shownAt = 0;
  let showTimer: ReturnType<typeof setTimeout> | undefined;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  const clear = () => {
    if (showTimer) clearTimeout(showTimer);
    if (hideTimer) clearTimeout(hideTimer);
    showTimer = hideTimer = undefined;
  };
  return {
    set(raw: boolean) {
      if (raw) {
        if (hideTimer) {
          clearTimeout(hideTimer);
          hideTimer = undefined;
        }
        if (shown || showTimer) return;
        showTimer = setTimeout(() => {
          showTimer = undefined;
          shown = true;
          shownAt = now();
          onChange(true);
        }, delayMs);
        return;
      }
      if (showTimer) {
        clearTimeout(showTimer);
        showTimer = undefined;
      }
      if (!shown || hideTimer) return;
      const remaining = minVisibleMs - (now() - shownAt);
      const hide = () => {
        hideTimer = undefined;
        shown = false;
        onChange(false);
      };
      if (remaining <= 0) hide();
      else hideTimer = setTimeout(hide, remaining);
    },
    get on() {
      return shown;
    },
    dispose: clear,
  };
}

/** Trailing debounce with an explicit flush (commit now) and cancel. */
export function createDebouncer<T>(fn: (v: T) => void, ms: number) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending: { v: T } | null = null;
  return {
    schedule(v: T) {
      pending = { v };
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = undefined;
        const p = pending;
        pending = null;
        if (p) fn(p.v);
      }, ms);
    },
    /** Run the pending call now (if any). */
    flush() {
      if (timer) clearTimeout(timer);
      timer = undefined;
      const p = pending;
      pending = null;
      if (p) fn(p.v);
    },
    cancel() {
      if (timer) clearTimeout(timer);
      timer = undefined;
      pending = null;
    },
    get pending() {
      return pending !== null;
    },
  };
}

/* ------------------------------ number input ------------------------------- */

export interface NumberBounds {
  type: string;
  min?: number;
  max?: number;
}

export type ParsedNumber =
  | { kind: "empty" }
  | { kind: "invalid" }
  /** `clamped` says which bound the typed value was pulled to; `value` is already clamped. */
  | { kind: "ok"; value: number; clamped?: "min" | "max" };

/** Parse what is typed in a number field against the param's bounds. Partial input ("4.", "-") is not a number yet. */
export function parseNumberInput(raw: string, spec: NumberBounds): ParsedNumber {
  const t = raw.trim();
  if (!t) return { kind: "empty" };
  if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(t)) return { kind: "invalid" };
  let n = Number(t);
  if (!Number.isFinite(n)) return { kind: "invalid" };
  if (spec.type === "int") n = Math.round(n);
  if (spec.min !== undefined && n < spec.min) return { kind: "ok", value: spec.min, clamped: "min" };
  if (spec.max !== undefined && n > spec.max) return { kind: "ok", value: spec.max, clamped: "max" };
  return { kind: "ok", value: n };
}

/** Slider fill for `--cfg-fill`, 0–100 (percent of the range covered by the value). */
export function fillPercent(value: number, min: number, max: number): number {
  if (!(max > min) || !Number.isFinite(value)) return 0;
  return Math.round(Math.min(1, Math.max(0, (value - min) / (max - min))) * 1000) / 10;
}

/* ------------------------------ reduced motion ----------------------------- */

export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** `scrollIntoView` behaviour that honours the reduced-motion preference. */
export function scrollBehavior(): ScrollBehavior {
  return prefersReducedMotion() ? "auto" : "smooth";
}
